import type { Locale } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ When something happened, in words — item 12.                              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`Intl.RelativeTimeFormat`, NOT A BUCKET OF OUR OWN STRINGS.** The platform has both
 * languages and gets the plural and the *"yesterday"* case right; writing them ourselves would be
 * two catalogue entries per bucket and a Hungarian plural rule we would have to be right about.
 *
 * ⚠️ **`numeric: 'auto'`** so yesterday reads *"tegnap"* rather than *"1 nappal ezelőtt"*, which is
 * the difference between a sentence and a stopwatch.
 *
 * ⚠️⚠️ **EXTRACTED FROM `use-notifications.tsx` FOR ITEM 12**, where the devices and passkeys lists
 * were printing a raw ISO instant — *"2026-09-01T09:14:22.331Z"* under a device name, which nobody
 * reads as *"this morning"*. The notification popover has done this correctly since C17; the
 * security page simply never asked it.
 */
export function relativeTime(iso: string, locale: Locale, now: number = Date.now()): string {
  const at = new Date(iso).getTime();
  /* ⚠️ An unparseable instant renders as itself rather than as `Invalid Date` — a wrong string is
     at least a string somebody can report, where `NaN` in a sentence looks like a crash. */
  if (!Number.isFinite(at)) return iso;

  /* ⚠️ Negative: everything here happened in the past, and `RelativeTimeFormat` reads the sign. */
  const seconds = Math.round((at - now) / 1000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  const minutes = Math.round(seconds / 60);
  if (Math.abs(seconds) < 60) return format.format(seconds, 'second');
  if (Math.abs(minutes) < 60) return format.format(minutes, 'minute');

  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return format.format(hours, 'hour');

  const days = Math.round(hours / 24);
  if (Math.abs(days) < 7) return format.format(days, 'day');
  if (Math.abs(days) < 30) return format.format(Math.round(days / 7), 'week');
  if (Math.abs(days) < 365) return format.format(Math.round(days / 30), 'month');
  return format.format(Math.round(days / 365), 'year');
}
